import { connect } from 'react-redux';
import {
  CodeBench,
  mapDispatchToCodeBenchProps
} from '@codametrix/shared-views';
import type { AnyAction } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';
import {
  getPrevOrNextCase,
  getWorkItemDetails,
  updateWorkItemDismissed
} from '../../actions/cases.action';
import { setBackToWorklistPath } from '../../actions/ui';
import {
  AmplifyCore,
  appServiceLines,
  conditionalUtils,
  configurationUtils,
  launchPageActions
} from '@codametrix/ui-common';
import history from '../../history';

const { invoiceUtils } = AmplifyCore;

function isCursorApiLocationState(
  state: unknown
): state is Partial<CMxCommonApp.CursorAPIContext> & Record<string, unknown> {
  return (
    state != null &&
    typeof state === 'object' &&
    !Array.isArray(state) &&
    ('servicedeskMode' in state ||
      'worklistContext' in state ||
      'workitemId' in state)
  );
}
type CodeBenchDispatch = ThunkDispatch<CMx.ApplicationState, unknown, AnyAction>;

//state = CMx.ApplicationState but not exactly
export const mapStateToCodeBenchProps = function(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  state: any
): Accelerate.BenchStateData {
  const location_state = history.location.state as Record<string, unknown> | undefined;
  const reduxContext = AmplifyCore.state.capabilities.cursorAPIContext;
  // Worklist / merge-case launches pass router state with cursor fields. Cases → Amplify
  // uses history.push without state; falling through to Redux would leave servicedeskMode
  // true after visiting Worklist (CDMX-15442).
  const cursorAPIContext = isCursorApiLocationState(location_state)
    ? { ...reduxContext, ...location_state }
    : {
        ...reduxContext,
        servicedeskMode: false,
        worklistContext: {}
      };

  const realState = state as CMx.ApplicationState;
  const coderConfig = realState.ampUI.coderConfig;

  // Use the utility function to filter source invoices
  const {
    filteredSourceInvoices,
  } = configurationUtils.filterSourceInvoices({
    coderConfig,
    sourceInvoices: realState.codeBench.sourceInvoices.sourceInvoices,
    activeItemArtifacts: realState.cases.activeItem.artifacts,
    tenantId: realState.ui.context.activeContext?.healthSystemTenantId || '',
    serviceLine: realState.ui.context.activeContext?.serviceLineName || ''
  });

  const suggestedCodesFromSourceInvoices = invoiceUtils.sourceInvoicesToCodeSourceList(
    filteredSourceInvoices,
    realState.codeBench.codeDescriptions || {}
  );

  const hasMultiCodeSetFeatures = conditionalUtils.capabilityValue(
    state.capabilities?.capabilities?.codeSourcesFeatures?.on
  );
  const suggestedCodes: CMxCommonApp.SuggestedCodes = {
    cpt: {
      ...realState.codeBench.suggestedCodes.cpt,
      codeSourceList: hasMultiCodeSetFeatures
        ? suggestedCodesFromSourceInvoices.cptSourceList
        : []
    },
    icd: {
      ...realState.codeBench.suggestedCodes.icd,
      codeSourceList: hasMultiCodeSetFeatures
        ? suggestedCodesFromSourceInvoices.icdSourceList
        : []
    },
    modifier: {
      ...realState.codeBench.suggestedCodes.modifier,
      codeSourceList: hasMultiCodeSetFeatures
        ? suggestedCodesFromSourceInvoices.modSourceList
        : []
    }
  };
  return {
    ...state.codeBench,
    ...state.ampUI,
    suggestedCodes,
    capabilities: state.capabilities,
    improvements: state.improvements,
    caseEdits: state.caseEdits?.caseEdits,
    cursorAPIContext,
    htmlNotes: state?.cases?.htmlNotes ?? state?.codeBench?.htmlNotes,
    roles: state?.cases?.roles ?? [],
    caseRun: state?.cases?.activeItem,
    caseAccessTime: state?.codeBench?.caseAccessTime,
    lockCaseUid: state?.codeBench?.lockCaseUid,
    customClaimsRelatedCases: state?.codeBench?.customClaimsRelatedCases,
    customClaimsRelatedCasesLoadState:
      state?.codeBench?.customClaimsRelatedCasesLoadState,
    backToWorklistPath: state.ui.backToWorklistPath,
    propertyConfig: state?.codeBench?.propertyConfig,
    workItem: state?.cases?.workItem,
    sourceInvoices: {
      sourceInvoices: filteredSourceInvoices
    }
  };
};

export const mapUpdatedStateToCodeBenchProps = (
  state: CMx.ApplicationState
) => {
  const values = mapStateToCodeBenchProps(state);

  const accountDefaults = state.accountSettings?.accountDefaults?.formObject;
  const demographicsPanelLocation = accountDefaults?.demographicsPanelLocation || 'right';
  const demographicsPanelVisibility = accountDefaults?.demographicsPanelVisibility || 'open';

  return {
    ...values,
    loadAlwaysFromURL: true,
    demographicsPanelLocation,
    demographicsPanelVisibility
  };
};

export const mapUpdatedDispatchToCodeBenchProps = (
  dispatch: CodeBenchDispatch
): Accelerate.CodeBenchPanelMethods => {
  const values = mapDispatchToCodeBenchProps(dispatch);
  return {
    ...values,
    openAmplifyCase: (
      amplifyLaunchConfig: CMxCommonApp.LaunchWorkItemConfig & { id?: string }
    ) => {
      dispatch(setBackToWorklistPath({ path: 'worklist/' }));
      const serviceLine = appServiceLines.toServiceLineSlug(
        appServiceLines.normalizeServiceLineName(
          String(amplifyLaunchConfig.serviceLine ?? '')
        )
      );
      const processId =
        amplifyLaunchConfig.processId ?? amplifyLaunchConfig.id;

      dispatch(
        launchPageActions.navigationIntent({
          intent: 'CASE_DETAIL',
          url: `/cmx/worklist/${serviceLine}/case/${
            amplifyLaunchConfig.caseId
          }/case-run/${processId}${
            amplifyLaunchConfig.hasAmplifyCaseRunV2Access ? '/v2' : ''
          }/`,
          state: history.location.state
        })
      );
    },
    onCursorAction: async (cursorAPIPayload: CMxCommonApp.CursorAPIPayload) => {
      dispatch(getPrevOrNextCase(cursorAPIPayload));
    },
    loadWorkItemDetails: async (case_uid: string, caserun_uid: string) => {
      dispatch(
        getWorkItemDetails({
          case_uid,
          caserun_uid
        })
      );
    },
    updateWorkItemDismissed: async (workItemId: string, dismissed: boolean) => {
      dispatch(updateWorkItemDismissed({ workItemId, dismissed }));
    }
  };
};

export const ConnectedCodeBench = connect<
  Accelerate.BenchStateData,
  Accelerate.CodeBenchPanelMethods
>(
  mapUpdatedStateToCodeBenchProps,
  mapUpdatedDispatchToCodeBenchProps
)(CodeBench);
